import { isRetryableError, toAppError } from '@helpers/Errors';
import { logError, markLoggedError } from '@helpers/Loggers';
import { MutationCache, QueryCache, QueryClient, QueryClientConfig } from '@tanstack/react-query';
import type { Mutation, Query } from '@tanstack/react-query';

export const APP_QUERY_DEFAULT_STALE_TIME_MS = 5 * 60 * 1000;
export const APP_QUERY_DEFAULT_GC_TIME_MS = 30 * 60 * 1000;
export const APP_QUERY_MAX_RETRIES = 2;

export type AppQueryClient = QueryClient;

export function createAppQueryClient(config: QueryClientConfig = {}): AppQueryClient {
  return new QueryClient({
    ...config,
    queryCache:
      config.queryCache ??
      new QueryCache({
        onError: (error, query) => safeLogQueryError(error, getQueryErrorMetadata(query)),
      }),
    mutationCache:
      config.mutationCache ??
      new MutationCache({
        onError: (error, variables, _onMutateResult, mutation) =>
          safeLogQueryError(error, getMutationErrorMetadata(mutation, variables)),
      }),
    defaultOptions: {
      ...config.defaultOptions,
      queries: {
        staleTime: APP_QUERY_DEFAULT_STALE_TIME_MS,
        gcTime: APP_QUERY_DEFAULT_GC_TIME_MS,
        retry: shouldRetryQuery,
        ...config.defaultOptions?.queries,
      },
      mutations: {
        retry: false,
        ...config.defaultOptions?.mutations,
      },
    },
  });
}

export function shouldRetryQuery(failureCount: number, error: unknown) {
  return failureCount < APP_QUERY_MAX_RETRIES && isRetriableQueryError(error);
}

export function isRetriableQueryError(error: unknown) {
  return isRetryableError(error);
}

export function logQueryError(error: unknown, metadata?: Record<string, unknown>) {
  markLoggedError(error);
  const appError = toAppError(error);
  if (metadata) {
    logError(appError, metadata);
  } else {
    logError(appError);
  }
}

export function safeLogQueryError(error: unknown, metadata?: Record<string, unknown>) {
  try {
    logQueryError(error, metadata);
  } catch {
    // Logging should never turn a handled query failure into a secondary app failure.
  }
}

export function normalizeQueryError(error: unknown): Error {
  return toAppError(error);
}

function getQueryErrorMetadata(
  query: Query<unknown, unknown, unknown, readonly unknown[]>
): Record<string, unknown> {
  return {
    queryHash: query.queryHash,
    queryKey: query.queryKey,
  };
}

function getMutationErrorMetadata(
  mutation: Mutation<unknown, unknown, unknown, unknown>,
  variables: unknown
): Record<string, unknown> {
  return {
    mutationKey: mutation.options.mutationKey,
    variables,
  };
}
